import React, { useMemo } from "react";
import { Volume2 } from "lucide-react";
import { Markdown } from "./Markdown";

interface ActivityNoteBoxProps {
  notes?: string | null;
  className?: string;
}

/**
 * Caixa de destaque estilizada para notas de atividades, transcrições e ligações.
 * Suporta tags HTML (<b>, <br>, <a>, etc.), Markdown (##, ###, *, 1., etc.), URLs automáticas
 * e reprodutor nativo HTML5 para gravações de áudio.
 */
export const ActivityNoteBox: React.FC<ActivityNoteBoxProps> = ({
  notes,
  className = "",
}) => {
  // Extrai URL de áudio caso presente nas notas
  const audioUrl = useMemo(() => {
    if (!notes) return null;
    const match = notes.match(
      /(https?:\/\/[^\s<)"']+(?:\.mp3|\.wav|\.ogg|call-recordings|attachments\/calls)[^\s<)"']*)/i,
    );
    return match ? match[1] : null;
  }, [notes]);

  // Pré-processa URLs puras que não estejam em links markdown/html para ficarem legíveis
  const processedNotes = useMemo(() => {
    if (!notes || !notes.trim()) return "";
    let text = notes;
    // Substitui URLs de áudio/gravação da api4com por link formatado se for url crua
    text = text.replace(
      /(?<!href=["'])(https?:\/\/[^\s<)]*(?:api4com\.com|recording|audio)[^\s<)]*)/gi,
      "[Clique aqui para ouvir a gravação]($1)",
    );
    return text;
  }, [notes]);

  if (!processedNotes) return null;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Intercepta cliques para não disparar ação do card pai (abrir modal)
    e.stopPropagation();

    // Se o clique foi em um link, garante abertura em nova aba com segurança
    const target = e.target as HTMLElement;
    const anchor = target.closest("a");
    if (anchor) {
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`w-full mt-2.5 px-3 py-2.5 rounded-lg bg-amber-50/90 dark:bg-amber-950/30 border border-amber-200/80 dark:border-amber-800/40 text-amber-950 dark:text-amber-200 text-xs shadow-xs transition-colors overflow-hidden select-text ${className}`}
    >
      <Markdown
        className={
          "text-xs leading-relaxed " +
          // Parágrafos
          "[&_p]:my-1.5 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 " +
          // Cabeçalhos Markdown (##, ###, ####)
          "[&_h1]:text-sm [&_h1]:font-bold [&_h1]:text-amber-950 [&_h1]:dark:text-amber-100 [&_h1]:mt-2.5 [&_h1]:mb-1 [&_h1:first-child]:mt-0 " +
          "[&_h2]:text-xs [&_h2]:font-bold [&_h2]:text-amber-950 [&_h2]:dark:text-amber-100 [&_h2]:mt-2.5 [&_h2]:mb-1 [&_h2]:border-b [&_h2]:border-amber-200/60 [&_h2]:dark:border-amber-800/50 [&_h2]:pb-0.5 [&_h2:first-child]:mt-0 " +
          "[&_h3]:text-[11px] [&_h3]:font-bold [&_h3]:text-amber-900 [&_h3]:dark:text-amber-200 [&_h3]:mt-2 [&_h3]:mb-0.5 [&_h3:first-child]:mt-0 " +
          "[&_h4]:text-[11px] [&_h4]:font-semibold [&_h4]:text-amber-900 [&_h4]:dark:text-amber-200 [&_h4]:mt-1.5 [&_h4]:mb-0.5 [&_h4:first-child]:mt-0 " +
          // Negrito e destaque
          "[&_b]:font-bold [&_b]:text-amber-950 [&_b]:dark:text-amber-100 " +
          "[&_strong]:font-bold [&_strong]:text-amber-950 [&_strong]:dark:text-amber-100 " +
          // Listas
          "[&_ul]:list-disc [&_ul]:ml-4 [&_ul]:my-1.5 [&_ul]:space-y-0.5 " +
          "[&_ol]:list-decimal [&_ol]:ml-4 [&_ol]:my-1.5 [&_ol]:space-y-0.5 " +
          "[&_li]:my-0.5 [&_li]:leading-relaxed " +
          // Links
          "[&_a]:text-sky-700 [&_a]:dark:text-sky-300 [&_a]:font-semibold [&_a]:underline [&_a]:hover:text-sky-800 [&_a]:dark:hover:text-sky-200 [&_a]:cursor-pointer [&_a]:break-all"
        }
      >
        {processedNotes}
      </Markdown>

      {audioUrl && (
        <div className="mt-3 pt-2.5 border-t border-amber-200/70 dark:border-amber-800/40">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-900 dark:text-amber-200 mb-1.5">
            <Volume2 className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
            <span>Ouvir Gravação da Ligação</span>
          </div>
          <audio
            controls
            src={audioUrl}
            className="w-full h-8 rounded"
            preload="metadata"
          />
        </div>
      )}
    </div>
  );
};
